<template>
  <div class="bom">
    <div class="module-header v-center space-between">
      <h1 class="module-name">Bill of Materials</h1>
      <div class="bom-action m flex">
        <button
          v-if="showCreateBom"
          class="btn-bulk-import mr-2"
          @click="isBulkImportModalOpen = true"
        >
          <import-icon className="bulk-import-icon" />
          Bulk Import
        </button>
        <modal
          :open="isBulkImportModalOpen"
          @close="isBulkImportModalOpen = false"
          :closeOnOutsideClick="true"
          title="Bulk Import BOM"
          description="Upload multiple Excel files and map columns to BOM fields"
        >
          <bulk-import-modal-content @close="isBulkImportModalOpen = false" />
        </modal>
        <create-bom
          @updatebomlist="updatebomlist"
          v-if="showCreateBom"
          :bomlistLength="bomList?.length"
        />
        <button @click="gotoBomCompare" class="btn btn-black mr-2" :disabled="bomList?.length === 0">
          Compare BOM
        </button>
        <button
          :disabled="!canCompare"
          @click="gotoVersionCompare"
          class="btn btn-black mr-2"
        >
          Compare BOM Version
        </button>
      </div>
    </div>
    <div class="bom-container">
      <transition name="sidebar">
      <div v-show="!showProductCode" class="bom-product-code" :class="{ 'bom-product-code-collapsed': !isSidebarOpen }">
        <project-product-list ref="productList" @handle-sidebar="handleSidebarToggle" />
      </div>
      </transition>
      <div :class="showProductCode ? 'bom-full-width':'bom-content'">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import createBom from '../../../components/bom/common/createBom.vue'
import ProjectProductList from '../../../components/bom/project/projectProductList.vue'
import { GetAllBomVersionListByBomId } from '@/api'
import modal from '@/components/common/modal.vue'
import ImportIcon from '@/assets/images/SVGcomponents/ImportIcon.vue'
import BulkImportModalContent from '@/components/bom/common/bulkImportModalContent.vue'
export default {
  components: { createBom, ProjectProductList, modal, ImportIcon, BulkImportModalContent },
  data () {
    return {
      canCompare: false,
      isSidebarOpen: true,
      bomListSidebar: { isOpen: true },
      isBulkImportModalOpen: false
    }
  },
  provide () {
    return {
      bomListSidebar: this.bomListSidebar
    }
  },
  computed: {
    ...mapGetters('projectBom', ['selectedBomId']),
    ...mapGetters(['user', 'collaborator', 'projectIdForCollaborator']),
    ...mapGetters('projectBom', ['bomList']),
    showProductCode () {
      return ((this.$route.path.includes('/bom') && this.$route.path.includes('/edit')) || (this.$route.path.includes('/bom') && this.$route.path.includes('/new')))
    },
    showCreateBom () {
      return ((this.user.projectLevelRole === 'ADMIN' ||
      this.user.projectLevelRole === 'COLLABORATOR' || this.user.projectLevelRole === 'EDITOR') && !this.collaborator)
    }
  },
  methods: {
    async checkBomCompare () {
      if (this.selectedBomId) {
        const res = await GetAllBomVersionListByBomId(this.selectedBomId)
        this.canCompare = res.bom_versions[0].version_no > 1
      }
    },
    gotoBomCompare () {
      this.$router.push('/bom/compare/project')
    },
    gotoVersionCompare () {
      this.$router.push(`/bom/compare/${this.selectedBomId}`)
    },
    setBaseData () {
      const { bomId } = this.$route.params
      const { bomVersionId } = this.$route.query
      this.$store.dispatch('projectBom/setBaseData', {
        bom_id: bomId || null,
        bom_version_id: bomVersionId || null
      })
    },
    async updatebomlist (productCode) {
      if (this.$refs.productList) { await this.$refs.productList.getAllProductCodeList() }
      if (productCode && String(productCode) !== String(this.$route.params.productCode)) {
        this.$router.push(`/bom/project/${productCode}`).catch(() => {})
      } else {
        this.$store.dispatch('projectBom/getBomList')
      }
    },
    handleSidebarToggle () {
      this.isSidebarOpen = !this.isSidebarOpen
    }
  },
  mounted () {
    this.checkBomCompare()
  },
  watch: {
    selectedBomId (newVal) {
      if (newVal) {
        this.checkBomCompare()
      }
    },
    '$store.state.projectIdForCollaborator' () {
      this.updatebomlist()
      if (this.$store.projectIdForCollaborator === null) {
        this.updatebomlist()
      }
    },
    '$route.params.bomId' (val) {
      this.setBaseData()
    },
    '$route.name' (newName, oldName) {
      if (oldName === 'NewProjectBom' && newName === 'ProjectBomDetail') {
        if (this.$refs.productList) { this.$refs.productList.getAllProductCodeList() }
      }
    },
    '$route.query.bomVersionId' (val) {
      this.setBaseData()
    },
    '$route.query.name' () {
      this.setBaseData()
      this.updatebomlist()
    }
  },
  created () {
    if (this.collaborator && !this.projectIdForCollaborator && this.$route.query?.projectId) {
      this.$router.push('/bom/product')
    } else {
      this.setBaseData()
      if (this.$route.params.productCode) {
        this.$store.dispatch('projectBom/getBomList')
      }
    }
  }
}
</script>

<style lang="scss" scoped >
.sidebar-enter-active,
.sidebar-leave-active {
  transition: width 0.3s ease, min-width 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.sidebar-enter,
.sidebar-leave-to {
  width: 0 !important;
  min-width: 0 !important;
  opacity: 0;
}

.btn-bulk-import {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(47, 128, 255, 0.1);
  color: #2f80ff;
  border: 1px solid rgba(47, 128, 255, 0.2);
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(47, 128, 255, 0.15);
  }

  .bulk-import-icon {
    width: 16px;
    height: 16px;
  }
}

.bom {
  height: 100%;
  &-container {
    height: calc(100% - 60px);
    display: flex;
    // padding-top: 10px;
    & > div {
      height: 100%;
    }
  }
  &-product-code {
    width: 20%;
    transition: width 0.3s ease, min-width 0.3s ease;

    @media screen and (max-width: 1366px) {
      width: 15%;
  }

  &.bom-product-code-collapsed {
    width: 32px;
    min-width: 32px;
  }
  }
  &-content {
    width: 85%;
    overflow-y: auto;
    flex: 1;
  }
  &-full-width {
    width: 100%;
  }
}

:deep(.modal-header .main-title) {
  font-size: var(--text-base);
  font-weight: 600;
  color: #333;
}

:deep(.modal-description) {
  font-size: var(--text-s);
  color: #666;
}
</style>
